import React from 'react';
import { FormField, Select, SelectProps, SpaceBetween, Textarea } from '@amzn/awsui-components-react';
import { BulkPromotionContext } from 'src/services/bulk-promotion/BulkPromotionContext';
import { ACTIONS } from 'src/services/bulk-promotion/BulkPromotionReducer';
import {
    BULK_PROMOTABLE_TYPES,
    BULK_PROMOTE_BATCH_CAP,
    getBulkPromoteTypeLabel,
} from 'src/models/promotion/bulkPromotionConstants';

/**
 * Type selector and paste box.
 *
 * Renders immediately: the option list and the cap are constants, so there is no configuration fetch in
 * front of the form — see BULK_PROMOTABLE_TYPES and BULK_PROMOTE_BATCH_CAP.
 *
 * Whether the environment can start a batch is answered at submit, where
 * BulkPromotionGateComponent.requireOperable raises a UserFacingException naming every unset setting and
 * the WebSocket handler passes that message through. One evaluation, so the page cannot report ready
 * while submit refuses.
 */
export default function BulkPromoteFields() {
  const { bulkPromotionState, bulkPromotionDispatch } = React.useContext(BulkPromotionContext);
  const {
    componentType, pastedIdentifiers, acceptedIds, loading,
  } = bulkPromotionState;

  const options: SelectProps.Option[] = BULK_PROMOTABLE_TYPES.map(promotableType => ({
    value: promotableType,
    label: getBulkPromoteTypeLabel(promotableType),
  }));
  const selectedOption = options.find(option => option.value === componentType) || null;

  return (
    <SpaceBetween direction="vertical" size="l">
      <FormField label="Component type" description="One component type per batch.">
        <div data-class="bulkPromoteTypeSelect" style={{ minWidth: '260px' }}>
          <Select
            selectedOption={selectedOption}
            options={options}
            disabled={loading}
            onChange={({ detail }) => detail.selectedOption.value
              && bulkPromotionDispatch(ACTIONS.SET_COMPONENT_TYPE.withPayload(detail.selectedOption.value))}
            placeholder="Select a component type"
          />
        </div>
      </FormField>
      <FormField
        data-class="calculationsTextArea"
        label="Component IDs"
        constraintText={`One ID per line. Up to ${BULK_PROMOTE_BATCH_CAP} per batch. ${acceptedIds.length} resolved.`}
      >
        <Textarea
          onChange={({ detail }) => bulkPromotionDispatch(ACTIONS.SET_PASTED_IDENTIFIERS.withPayload(detail.value))}
          value={pastedIdentifiers}
          rows={7}
          disabled={loading || !componentType}
          placeholder="Paste one component ID or page URL per line"
        />
      </FormField>
    </SpaceBetween>
  );
}
